import useAsclepius from "@common/composables/api/asclepius"
import { keepPreviousData, useQuery } from "@tanstack/vue-query"
import { computed, ref, watch } from "vue"
import type { Company, ListCompaniesResponse } from "@vitable/asclepius-api-client-ts"

import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"

export const useListCompanies = () => {
  const { getCompanyApi, callWithResponse } = useAsclepius()
  const name = ref("")
  const page = ref(1)

  const query = useQuery({
    queryKey: [QueryApiCacheKeys.VITABLE_ADMIN_EMPLOYERS, name, page],
    queryFn: () =>
      callWithResponse<ListCompaniesResponse>(() =>
        getCompanyApi().listCompanies({
          activeIn: true,
          name: name.value || undefined,
          page: page.value
        })
      ),
    placeholderData: keepPreviousData,
    staleTime: 30_000
  })

  const companies = computed<Array<Company>>(() => query.data.value?.results ?? [])
  const companiesCount = computed(() => query.data.value?.count ?? 0)
  const errorMessage = computed(() => (query.error.value ? "Employers couldn't be loaded. Try again." : null))

  return {
    query,
    name,
    page,
    companies,
    companiesCount,
    errorMessage
  }
}

export const useGetCompany = () => {
  const { getCompanyApi, callWithResponse, callResult } = useAsclepius()

  const company = ref<Nullable<Company>>(null)

  async function getCompany(companyId: string) {
    await callWithResponse(() =>
      getCompanyApi().getCompany({
        companyId
      })
    )
    company.value = callResult.value.getData().company
  }

  return {
    company,
    getCompany,
    getCompanyResult: callResult
  }
}

export const useGetEmployerCompany = () => {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  const company = ref<Nullable<Company>>(null)

  async function getEmployerCompany() {
    await callWithResponse(() => getAuthenticationApi().getEmployerContext())
    company.value = callResult.value.getData().company
  }

  return {
    company,
    getEmployerCompany,
    getEmployerCompanyResult: callResult
  }
}

export const useGetPublicCompanyDetails = () => {
  const { getCompanyApi, callWithResponse, callResult } = useAsclepius()

  const companyDetails = ref(null)

  async function getPublicCompanyDetails(publicCompanyId: number) {
    await callWithResponse(() =>
      getCompanyApi().getPublicCompanyDetails({
        publicCompanyId
      })
    )
  }

  watch(callResult, () => (companyDetails.value = callResult.value.isSuccess() ? callResult.value.getData() : null))

  return {
    getPublicCompanyDetails,
    getPublicCompanyDetailsResult: callResult,
    companyDetails
  }
}
